import { computed } from '@vue/composition-api'
import { INPUT_TYPES_TO_FILTER_TYPES_MAP } from '@/constants/'
import store from '@/store'

const defaultProgramQuestions = computed(() => store.state.curriculumPrograms.programTemplate.programTemplate.questions)

export function useCourseFilterFields (questions) {
  const filterFields = computed(() => questions.value.reduce((acc, question) => {
    acc.push({
      name: question.dataKey,
      label: question.label,
      group: 'course',
      inputType: question.questionType,
      custom: question.custom,
      ...getAdditionalCourseFieldProps(question),
    })
    return acc
  }, [])
    .filter(f => Object.keys(INPUT_TYPES_TO_FILTER_TYPES_MAP).includes(f.inputType))
    .sort((fieldA, fieldB) => {
      const aKey = fieldA.label || fieldA.name
      const bKey = fieldB.label || fieldB.name
      return aKey.localeCompare(bKey)
    }))
  return filterFields
}

function getAdditionalCourseFieldProps (question) {
  const multiple = question.config && question.config.multiple
  switch (question.questionType) {
    case 'textarea':
    case 'courseSelect':
    case 'organizationSelect':
    case 'userSelect':
    case 'termSelect':
      return {
        inputType: 'text',
        props: {
          config: {
            multiple,
          },
        },
      }
    case 'courseNumber':
      return {
        inputType: 'text',
      }
    case 'checkbox':
    case 'yesNo':
      return { inputType: 'boolean' }
    case 'careerSelect':
    case 'requirementDesignationSelect':
    case 'milestoneSelect':
    case 'subjectCodeSelect':
    case 'cipCodeSelect':
    case 'hegisCodeSelect':
    case 'gradeModeSelect':
    case 'gradeModesMultiSelect':
    case 'campusSelect':
    case 'collegeSelect':
    case 'attributeSelect':
    case 'select':
      return {
        // This setting is used to indicate that we will be replacing the select input with a "text" input for the contains filter
        showTextForContains: !multiple,
        props: {
          config: {
            options: question.config && question.config.options,
            multiple: multiple,
          },
        },
      }
    case 'departmentSelect':
      return {
        inputType: 'select',
        props: {
          customLabel: departmentId => store.getters['departments/departmentDisplayName'](departmentId),
          config: {
            ...question.config,
            options: store.getters['departments/departmentsArray'],
            multiple: true,
          },
        },
      }
    case 'componentSelect':
      return {
        showTextForContains: !multiple,
        props: {
          config: {
            options: question.config && question.config.options,
            multiple: multiple,
          },
          // For the component select, we must load all options, as we can't show options associated with a specific course
          loadOptions: true,
        },
      }
    default:
      return { }
  }
}

export function useProgramFilterFields (questions = defaultProgramQuestions) {
  const filterFields = computed(() => Object.values(questions.value).reduce((acc, question) => {
    acc.push({
      name: question.dataKey,
      label: question.label,
      group: 'program',
      inputType: question.questionType,
      custom: question.custom,
      ...getAdditionalProgramFieldProps(question),
    })
    return acc
  }, [])
    .filter(f => Object.keys(INPUT_TYPES_TO_FILTER_TYPES_MAP).includes(f.inputType))
    .sort((fieldA, fieldB) => {
      const aKey = fieldA.label || fieldA.name
      const bKey = fieldB.label || fieldB.name
      return aKey.localeCompare(bKey)
    }))
  return filterFields
}

export function useStudentFilterFields (questions) {
  const filterFields = computed(() => questions.value.reduce((acc, question) => {
    acc.push({
      name: question.questionId,
      label: question.label,
      group: 'student',
      inputType: question.questionType,
      custom: question.custom,
    })
    return acc
  }, [])
    .filter(f => Object.keys(INPUT_TYPES_TO_FILTER_TYPES_MAP).includes(f.inputType))
    .sort((fieldA, fieldB) => {
      const aKey = fieldA.label || fieldA.name
      const bKey = fieldB.label || fieldB.name
      return aKey.localeCompare(bKey)
    }))
  return filterFields
}

function getAdditionalProgramFieldProps (question) {
  const multiple = question.config && question.config.multiple
  switch (question.questionType) {
    case 'textarea':
    case 'courseSelect':
    case 'organizationSelect':
    case 'userSelect':
    case 'termSelect':
      return {
        inputType: 'text',
        props: {
          config: {
            multiple,
          },
        },
      }
    case 'checkbox':
    case 'yesNo':
      return { inputType: 'boolean' }
    case 'careerSelect':
    case 'fieldOfStudySelect':
    case 'campusSelect':
    case 'cipCodeSelect':
    case 'hegisCodeSelect':
    case 'collegeSelect':
    case 'degreeSelect':
    case 'select':
      return {
        // This setting is used to indiccate that we will be replacing the select input with a "text" input for the contains filter
        showTextForContains: !multiple,
        props: {
          config: {
            options: question.config && question.config.options,
            multiple: question.config && question.config.multiple,
          },
        },
      }
    case 'departmentSelect':
      return {
        inputType: 'select',
        props: {
          customLabel: departmentId => store.getters['departments/departmentDisplayName'](departmentId),
          config: {
            ...question.config,
            options: store.getters['departments/departmentsArray'],
            multiple: true,
          },
        },
      }
    default:
      return { }
  }
}
